import React from 'react';

import AampeItalicText from '../../components/shared/AampeText/AampeItalicText';
import styled from 'styled-components';

import aampeTheme from '../../constants/aampeTheme';

import { ResponsiveCSSProperties } from '../../models/responsive-css.model';

interface SearchInputProps {
    name?: string;
    label?: string;
    value: string;
    setValue?: (val: string) => void;
    onChange?: (e: any) => void;
    onBlur?: (e: any) => void;
    placeholder?: string;
    labelStyle?: React.CSSProperties;
    inputStyle?: ResponsiveCSSProperties;
    wrapperStyle?: React.CSSProperties;
    description?: string;
    isRequired?: boolean;
    fieldType?: 'text' | 'number' | 'file' | 'radio'; // add more
    leadingIcon?: React.ReactNode;
    trailingIcon?: React.ReactNode;
    className?: string;
}

const SearchInput: React.FC<React.PropsWithChildren<SearchInputProps>> = ({
    name,
    label,
    value = '',
    setValue,
    onChange,
    onBlur,
    placeholder = '',
    labelStyle,
    inputStyle,
    wrapperStyle,
    description = null,
    isRequired = false,
    fieldType = 'text',
    leadingIcon,
    trailingIcon,
    className,
}) => {
    return (
        <div style={{ ...wrapperStyle }}>
            {label && (
                <label
                    style={{
                        display: 'block',
                        fontSize: '14px',
                        fontWeight: '500' as any,
                        marginBottom: aampeTheme.spacing(1),
                        ...labelStyle,
                    }}
                >
                    {label}
                    {isRequired ? <AampeItalicText text="(Required)" /> : null}
                </label>
            )}
            <div style={{ position: 'relative' }} className={className}>
                {leadingIcon && <LeadingIconDiv>{leadingIcon}</LeadingIconDiv>}
                <Input
                    onChange={
                        onChange
                            ? onChange
                            : (e: any) => setValue?.(e.target.value)
                    }
                    onBlur={onBlur}
                    placeholder={placeholder}
                    leadingIcon={!!leadingIcon}
                    trailingIcon={!!trailingIcon}
                    inputStyle={inputStyle}
                    type={fieldType}
                    value={value}
                    name={name}
                />
                {trailingIcon && (
                    <TrailingIconDiv>{trailingIcon}</TrailingIconDiv>
                )}
            </div>
            {description ? <Description>{description}</Description> : null}
        </div>
    );
};

const Description = styled.p(() => ({
    color: 'gray',
    fontSize: aampeTheme.size.font.textSmall,
    marginBottom: aampeTheme.spacing(0.5),
}));

const LeadingIconDiv = styled.div(() => ({
    position: 'absolute',
    inset: 0,
    left: 0,
    display: 'flex',
    alignItems: 'center',
    marginLeft: aampeTheme.spacing4(3),
    marginRight: aampeTheme.spacing4(2),
    pointerEvents: 'none',
    width: aampeTheme.size.icon.normal,
}));

const TrailingIconDiv = styled.div(() => ({
    position: 'absolute',
    right: 0,
    top: 0,
    bottom: 0,
    display: 'flex',
    alignItems: 'center',
    paddingRight: aampeTheme.spacing(1),
    pointerEvents: 'none',
}));
const Input = styled.input.withConfig({
    shouldForwardProp: (prop) =>
        !['leadingIcon', 'trailingIcon', 'inputStyle'].includes(prop),
})<{
    leadingIcon?: boolean;
    trailingIcon?: boolean;
    inputStyle?: React.CSSProperties;
}>(({ leadingIcon, trailingIcon, inputStyle }) => ({
    backgroundColor: 'none',
    boxSizing: 'border-box',
    color: aampeTheme.colors.textAccessibleWG01,
    '::placeholder': {
        color: aampeTheme.colors.textAccessibleWG01,
        fontWeight: aampeTheme.weights.light,
    },
    borderRadius: aampeTheme.borderRadius.default,
    border: `1px solid ${aampeTheme.colors.interactiveWG01}`,
    padding: aampeTheme.spacing4(1.25),
    paddingLeft: leadingIcon ? aampeTheme.spacing4(10) : aampeTheme.spacing4(4),
    paddingRight: trailingIcon
        ? aampeTheme.spacing4(10)
        : aampeTheme.spacing4(3),
    width: '360px',
    height: '36px',
    fontSize: aampeTheme.size.font.default,
    fontFamily: 'inherit',
    lineHeight: '24px',
    fontWeight: aampeTheme.weights.light,
    '@media (max-width: 1439px)': {
        width: '300px',
    },
    '&:hover': {
        border: `1px solid ${aampeTheme.colors.textAccessibleWG01}`,
    },
    '&:focus': {
        outline: '0px',
        border: `1px solid ${aampeTheme.colors.solidA02}`,
    },

    ...(inputStyle ?? {}),
}));

export default SearchInput;
